import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { LAB_ROWS, LAB_REPORTS } from "../lib/data";
import { useLang } from "../lib/i18n";

const EASE = [0.16, 1, 0.3, 1];
const MARK = 62;

function ResultCell({ value, num, reqNum, testid }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const pct = Math.min(96, (num / reqNum) * MARK);
  return (
    <div ref={ref} className="min-w-[10rem]" data-testid={testid}>
      <div className="flex items-center gap-2">
        <span className="font-display text-sm font-bold text-cream">{value}</span>
        <motion.span
          initial={{ scale: 0, opacity: 0 }}
          animate={inView ? { scale: 1, opacity: 1 } : {}}
          transition={{ delay: 1.0, type: "spring", stiffness: 400, damping: 18 }}
          className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500/20"
        >
          <Check size={12} strokeWidth={3.5} className="text-emerald-400" />
        </motion.span>
      </div>
      <div className="relative mt-2 h-1.5 rounded-full bg-cream/10">
        <motion.div
          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-brass/70 to-brasslight"
          initial={{ width: 0 }}
          animate={inView ? { width: `${pct}%` } : {}}
          transition={{ duration: 1.1, ease: EASE }}
        />
        <span
          className="absolute -inset-y-[3px] w-px bg-cream/40"
          style={{ left: `${MARK}%` }}
          title="PS-232-2008 requirement"
        />
      </div>
    </div>
  );
}

export default function LabTable() {
  const { t } = useLang();
  return (
    <div className="mt-28" data-testid="lab-results-section">
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE }}
      >
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
          {t.labPre} <em className="font-serif italic font-medium text-brasslight">{t.labAccent}</em>
        </h2>
        <p className="mt-4 max-w-2xl text-base md:text-lg text-taupe">{t.labSub}</p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 36 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className="mt-12 overflow-x-auto rounded-[2rem] bg-espresso border border-cream/5 shadow-[0_30px_80px_rgba(0,0,0,0.35)]"
      >
        <table className="w-full min-w-[820px] text-left rtl:text-right" data-testid="lab-results-table">
          <thead>
            <tr className="border-b border-cream/10">
              {t.labHeaders.map((h) => (
                <th key={h} className="px-6 py-5 font-display text-xs font-bold uppercase text-taupe">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {LAB_ROWS.map((r, i) => (
              <motion.tr
                key={i}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.55, delay: i * 0.07, ease: EASE }}
                className="border-b border-cream/5 last:border-0 transition-colors duration-300 hover:bg-cream/[0.03]"
              >
                <td className="px-6 py-5 text-sm font-semibold text-cream/90">{t.labRows[i].test}</td>
                <td className="px-6 py-5 text-sm font-semibold text-taupe">{t.labRows[i].req}</td>
                <td className="px-6 py-5">
                  <ResultCell value={r.lucky} num={r.luckyNum} reqNum={r.reqNum} testid={`lab-lucky-${i}`} />
                </td>
                <td className="px-6 py-5">
                  <ResultCell value={r.bestway} num={r.bestwayNum} reqNum={r.reqNum} testid={`lab-bestway-${i}`} />
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </motion.div>

      <div className="mt-6 space-y-2 text-xs text-taupe">
        {t.labCaptions.map((caption, i) => (
          <p key={i}>
            {caption}
            <a
              href={LAB_REPORTS[i]}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={i === 0 ? "lucky-full-report-link" : "bestway-full-report-link"}
              className="ms-2 inline-flex items-center gap-1 font-semibold text-brasslight hover:text-cream transition-colors"
            >
              {t.viewReport} <ArrowUpRight size={12} className="rtl:-scale-x-100" />
            </a>
          </p>
        ))}
      </div>
    </div>
  );
}
